import { useSyncExternalStore } from 'react';

import { userManager } from '@/auth/oidcConfig';
import { hasUnsavedWork } from '@/auth/unsavedWork';

// ---------------------------------------------------------------------------
// Re-auth coordinator — the SINGLE home of the rule "the session is gone → get
// it back" (LOCKED plan 2026-08-24-auth-session-recovery, W1/A).
//
// Before this module the rule lived in two places with two different
// behaviours: `AuthGate` rendered an ErrorPane with a manual "Obnovit" button,
// and the axios 401 handler fired a bare `signinRedirect()`. The user-visible
// symptom was the confusing "Načtení uživatelského profilu selhalo" screen
// after a laptop sleep (KC SSO session gone → silent renew dead).
//
// Guarantees:
//   1. Single-flight — five concurrent 401s trigger exactly one recovery.
//   2. Visibility gate — a hidden tab NEVER redirects. The KC login page has
//      its own ~30 min expiry, so redirecting a background tab produces a dead
//      login form ("Přihlášení vypršelo, zkuste znovu"). Recovery is deferred
//      to the next `visibilitychange` that makes the tab visible.
//   3. Unsaved-work protection — with a dirty form on screen we do NOT redirect
//      silently (the redirect reloads the page and throws the edits away).
//      We publish `prompt`; the UI asks, and only the user's click redirects.
//   4. `returnTo` — the user lands back on the page they were on, not HOME.
//   5. Loop guard — one attempt per window; a second failure inside the window
//      surfaces a real error (KC genuinely down) instead of redirect-looping.
//      It arms only for RECOVERIES (a session that existed and died). A cold
//      start has nothing to loop on — nobody comes back from Keycloak without
//      either a session or an explicit error — and guarding it produced a fake
//      "service unavailable" for anyone who hit Back on the login page.
// ---------------------------------------------------------------------------

// Shared with AuthGate's stale-callback self-heal — deliberately ONE key and
// one guard implementation for the whole app (the plan forbids a second one).
// localStorage (not sessionStorage) so the marker survives the redirect
// round-trip in every browser context, including an Android standalone launch.
// NF-07-safe: a timestamp, never a token.
export const RECOVERY_KEY = 'auth.recoveryAt';
export const RECOVERY_WINDOW_MS = 30_000;

export const recoveryAllowed = (): boolean => {
  const last = Number(window.localStorage.getItem(RECOVERY_KEY) ?? 0);
  return Date.now() - last > RECOVERY_WINDOW_MS;
};

export const markRecoveryAttempt = (): void => {
  window.localStorage.setItem(RECOVERY_KEY, String(Date.now()));
};

export const clearRecoveryMarker = (): void => {
  window.localStorage.removeItem(RECOVERY_KEY);
};

// `deferred`  — tab is hidden; we are waiting for it to become visible.
// `recovering`— redirect to Keycloak is in flight (or about to be).
// `prompt`    — unsaved work on screen; waiting for the user's "Pokračovat".
// `failed`    — loop guard tripped or the redirect itself threw → real error.
export type ReauthState = 'idle' | 'deferred' | 'recovering' | 'prompt' | 'failed';

let state: ReauthState = 'idle';
const listeners = new Set<() => void>();
// Sticky: once a recovery has been requested it is NOT re-run for every further
// 401. Cleared only by `resetReauth()` (successful login, or an explicit retry).
let flight: Promise<void> | null = null;
let visibilityHandler: (() => void) | null = null;

// Escalation signals. With unsaved work on screen the loop guard is
// deliberately never consulted (a prompt cannot loop — it waits for a click),
// so these are the ONLY evidence that the redirect keeps failing.
//
// It matters because `signinRedirect()` does not reject when Keycloak is down:
// its metadata is cached in the UserManager singleton after the first load, so
// the call happily navigates to a dead host. The user gets a browser error
// page, presses Back, we restore and re-prompt — forever, with no explanation.
// We say so IN the prompt rather than switching to `failed`, which would
// replace the page and destroy the text.
//
// But "came back" alone is NOT evidence of an outage: someone who clicks
// Pokračovat, thinks better of it at the login form and presses Back has come
// back too, and telling them the service is down would be a lie with actively
// bad advice attached ("try again in a moment"). The two are told apart by how
// long the round trip took: a dead host fails and returns almost instantly,
// whereas a human reading a login form does not. A fast return is conclusive on
// its own; a slow one only counts once it has happened repeatedly.
//
// Both counters are module state, so a full page reload resets them — after a
// reload the user simply gets one un-escalated prompt again before we re-earn
// the evidence. That is acceptable: the escalation is advisory copy, not a gate.
const FAST_RETURN_MS = 1_500;
let promptAttempts = 0;
let fastReturns = 0;

const notify = (): void => {
  listeners.forEach((l) => {
    l();
  });
};

const setState = (next: ReauthState): void => {
  const wasEscalated = isReauthEscalated();
  if (next === 'prompt') promptAttempts += 1;
  const stateChanged = state !== next;
  state = next;
  // The second clause is defence, not a live path: today every escalation flip
  // happens on the way back to 'prompt' from 'recovering', so `stateChanged` is
  // already true and this never decides anything on its own. It is kept so that
  // a future path which re-lands on 'prompt' without passing through another
  // state still re-renders the banner instead of silently keeping stale copy.
  if (stateChanged || isReauthEscalated() !== wasEscalated) notify();
};

export const getReauthState = (): ReauthState => state;

// True once we have real evidence the login page is not reachable: either one
// round trip that came back too fast to have been a human, or three prompts.
// Primitive snapshot, so it is a safe `useSyncExternalStore` value.
export const isReauthEscalated = (): boolean => fastReturns > 0 || promptAttempts >= 3;

export const subscribeReauth = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

// React binding. `useSyncExternalStore` keeps the render tearing-free without
// pulling the coordinator into Redux (it must stay callable from axios).
export const useReauthState = (): ReauthState =>
  useSyncExternalStore(subscribeReauth, getReauthState, getReauthState);

export const useReauthEscalated = (): boolean =>
  useSyncExternalStore(subscribeReauth, isReauthEscalated, isReauthEscalated);

// Where the user should land after Keycloak bounces them back. Deliberately
// `pathname + search` (no hash, no origin) — `oidcConfig.onSigninCallback`
// re-validates it as a safe internal path before navigating.
const currentReturnTo = (): string => window.location.pathname + window.location.search;

// Set when the browser restores this page from the bfcache — i.e. the user
// pressed Back on the Keycloak login page. Registered at module evaluation,
// which is BEFORE oidc-client-ts adds its own `pageshow` listener inside
// `signinRedirect()`; same-event listeners run in registration order, so this
// flag is always already true by the time the awaited redirect resolves.
//
// Requiring the flag (rather than trusting the resolution alone) keeps the
// restart honest: a resolution WITHOUT a restore means no navigation happened,
// and restarting on that would be an unbounded automatic loop.
let restoredFromBfcache = false;
window.addEventListener('pageshow', (event) => {
  if ((event as PageTransitionEvent).persisted) restoredFromBfcache = true;
});

const waitForVisible = (): Promise<void> =>
  new Promise((resolve) => {
    if (visibilityHandler) document.removeEventListener('visibilitychange', visibilityHandler);
    visibilityHandler = () => {
      if (document.visibilityState !== 'visible') return;
      if (visibilityHandler) document.removeEventListener('visibilitychange', visibilityHandler);
      visibilityHandler = null;
      resolve();
    };
    document.addEventListener('visibilitychange', visibilityHandler);
  });

const redirectToLogin = async (isRecovery = true): Promise<void> => {
  let startedAt = 0;
  setState('recovering');
  // Only a recovery arms the guard. Marking a cold-start login would make an
  // ordinary "Back from the Keycloak login page" look like a redirect loop.
  if (isRecovery) markRecoveryAttempt();
  try {
    // Arm for THIS redirect. Without the reset the flag only ever meant
    // "a restore happened at some point since the last consume"; the comment
    // below promises "during this redirect", and this is what makes that true.
    // It is also the one safeguard against the unbounded-restart class, so it
    // should hold precisely rather than approximately.
    restoredFromBfcache = false;
    startedAt = Date.now();
    await userManager.signinRedirect({ state: { returnTo: currentReturnTo() } });
  } catch {
    // The redirect itself can fail (offline, KC unreachable). That is a real
    // failure — surface it rather than spinning forever.
    setState('failed');
    return;
  }
  // REACHING HERE WITH `restoredFromBfcache` MEANS THE USER CAME BACK.
  //
  // `signinRedirect()` hands off to oidc-client-ts's `RedirectNavigator`, whose
  // navigate promise resolves ONLY from a `pageshow` listener
  // (node_modules/oidc-client-ts/.../oidc-client-ts.js: `window.addEventListener
  // ("pageshow", () => resolve(window.location.href))`). A forward navigation
  // never resolves it — the page unloads. So a resolution paired with a
  // `persisted` pageshow is a bfcache restore: the browser put this page back
  // after a Back press on the Keycloak login page.
  //
  // Without this, the parked `flight` stays non-null forever (`flight ??=`),
  // `reauthState` sticks on `recovering`, nothing re-renders, and the user is
  // left staring at a fully drawn but sessionless app where every action 401s
  // — reachable only by a manual reload. Start over instead: drop the flight
  // and re-evaluate from scratch (visibility, unsaved work, guard).
  if (!restoredFromBfcache) return;
  restoredFromBfcache = false;
  // Too fast to have been a person at a login form ⇒ the host never answered.
  if (Date.now() - startedAt < FAST_RETURN_MS) fastReturns += 1;
  flight = null;
  void requestReauth({ recovery: isRecovery });
};

// `unsavedOverride` exists because of a React lifecycle race: when the session
// dies, AuthGate stops rendering its children and React runs their unmount
// cleanups (which unregister their unsaved-work keys) in the SAME commit whose
// passive effect asks us to recover. Read live from that effect and the
// registry is already empty — the dirty editor would be silently redirected
// away. AuthGate therefore snapshots `hasUnsavedWork()` during its last
// authenticated render, while the editor is still mounted, and passes it here.
// Callers outside React (the axios 401 handler) pass nothing and get the live
// read, which is correct for them.
const run = async (unsavedOverride?: boolean, isRecovery = true): Promise<void> => {
  if (document.visibilityState !== 'visible') {
    setState('deferred');
    await waitForVisible();
  }

  // UNSAVED WORK OUTRANKS THE LOOP GUARD, deliberately.
  //
  // The guard exists to stop AUTOMATIC redirect loops; a prompt is not
  // automatic — it parks until the user clicks, so it cannot loop. And the
  // guard's alternative (`failed` → a full-screen error pane) would tear the
  // page down, destroying the very edits the user pressed Back to rescue.
  // Checking the guard first would make the headline flow of this whole change
  // — "come back, grab my text" — end in an error pane with the text gone.
  if (unsavedOverride ?? hasUnsavedWork()) {
    // The prompt tells the truth (the edits will not survive the redirect) and
    // hands the timing to the user.
    setState('prompt');
    return;
  }

  // Nothing to lose: a second recovery inside the window is a genuine outage.
  // Cold starts are exempt — see the note on guarantee 5.
  if (isRecovery && !recoveryAllowed()) {
    setState('failed');
    return;
  }

  await redirectToLogin(isRecovery);
};

// Entry point for every "we lost the session" site: the axios 401 handler and
// AuthGate. Safe to call from anywhere, any number of times.
export interface RequestReauthOptions {
  // Overrides the live `hasUnsavedWork()` read — see `run`'s note on the
  // unmount race. Only AuthGate needs it.
  unsavedWork?: boolean;
  // False for a cold start (this tab never had a session). Defaults to true:
  // every caller outside AuthGate — notably the axios 401 handler — is by
  // definition recovering from a session that DID exist.
  recovery?: boolean;
}

// Referenced by `redirectToLogin` above. That is a `const` arrow declared
// earlier in the module, so this is a use-before-declaration in source order but
// NOT at runtime: `redirectToLogin` only reaches it after an awaited redirect
// resolves, long after the module has finished evaluating.
export const requestReauth = (options?: RequestReauthOptions): Promise<void> => {
  flight ??= run(options?.unsavedWork, options?.recovery ?? true);
  return flight;
};

// The unsaved-work prompt's button. Redirects regardless of `hasUnsavedWork()`
// — the user has just been told the edits will not be kept.
export const continueReauth = (): Promise<void> => {
  flight = redirectToLogin();
  return flight;
};

// Drops the sticky flight + state. Called when a login actually succeeds and
// from the error pane's manual retry.
export const resetReauth = (): void => {
  restoredFromBfcache = false;
  promptAttempts = 0;
  fastReturns = 0;
  if (visibilityHandler) {
    document.removeEventListener('visibilitychange', visibilityHandler);
    visibilityHandler = null;
  }
  flight = null;
  setState('idle');
};
